// SubmitTimesheet.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { WeekPicker } from "./weekpicker";
import moment from "moment";
import useHandleErrors from "../../utils/user.utils";
import { ToastContainer, toast } from "react-toastify";
import { useUser } from "../../utils/userContext";
import { useNavigate } from "react-router-dom";

const SubmitTimesheet = () => {
  const [weekStartDate, setWeekStartDate] = useState("");
  const [weekEndDate, setWeekEndDate] = useState("");
  const [projects, setProjects] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [startDate, setStartDate] = useState(moment().startOf("week").toDate());
  const [endDate, setEndDate] = useState(moment().endOf("week").toDate());
  const [loading, setLoading] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [dailyComments, setDailyComments] = useState(Array(7).fill(""));
  const [tempComment, setTempComment] = useState(""); 
  const { user } = useUser();
  const navigate = useNavigate();
  const handleErrors = useHandleErrors();
  const [filter, setFilter] = useState("all");
  const token = localStorage.getItem("token");

  const filteredProjects = projects.filter((project) => {
    if (filter === "all") return true;
    return (
      project.projectType ===
      (filter === "General & Administration"
        ? "General & Administration"
        : "Projects")
    );
  });

  const handleFilterChange = (e) => {
    setFilter(e.target.id);
  };

  useEffect(() => {
    const currentDate = new Date();
    const start = new Date(
      currentDate.setDate(currentDate.getDate() - currentDate.getDay())
    );
    const end = new Date(
      currentDate.setDate(currentDate.getDate() - currentDate.getDay() + 6)
    );

    setWeekStartDate(start.toISOString().split("T")[0]);
    setWeekEndDate(end.toISOString().split("T")[0]);
  }, []);

  const handleInputChange = (projectCode, dayIndex, value) => {
    setProjects((prevProjects) => {
      const updatedProjects = prevProjects.map((project) => {
        if (project.projectCode === projectCode) {
          const updatedWeekDays = [...project.weekDays];
          const hours = parseFloat(value) || 0;

          if (hours < 0) {
            toast.info("Negative hours are not allowed.", {
              position: toast.POSITION.TOP_CENTER,
            });
            updatedWeekDays[dayIndex] = { hours: 0 };
          } else if (hours > 24) {
            toast.info("Cannot submit more than 24 hours in a day.", {
              position: toast.POSITION.TOP_CENTER,
            });
            updatedWeekDays[dayIndex] = { hours: "" };
          } else {
            updatedWeekDays[dayIndex] = { hours };
          }
          return { ...project, weekDays: updatedWeekDays };
        }
        return project;
      });
      calculateTotalHours(updatedProjects);
      return updatedProjects;
    });
  };

  const handleCommentChange = (value) => {
    setTempComment(value); 
  };

  const calculateTotalHours = (projects) => {
    const total = projects.reduce((sum, project) => {
      if (project.weekDays) {
        return (
          sum +
          project.weekDays.reduce(
            (projSum, day) => projSum + (day.hours || 0),
            0
          )
        );
      }
      return sum;
    }, 0);
    setTotalHours(total);
  };

  const getWeekNumber = (weekStartDate) => {
    const startDate = new Date(weekStartDate);
    const oneJan = new Date(startDate.getFullYear(), 0, 1);
    const numberOfDays = Math.floor(
      (startDate - oneJan) / (24 * 60 * 60 * 1000)
    );
    const weekNumber = Math.ceil((startDate.getDay() + 1 + numberOfDays) / 7);
    return weekNumber;
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (totalHours === 0) {
      toast.error("Enter Timesheet Hours.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }

    // Validate that comments are provided for days with non-zero hours
    const daysWithHours = new Set();
    projects.forEach((project) => {
      project.weekDays.forEach((day, index) => {
        if ((day.hours || 0) > 0) {
          daysWithHours.add(index);
        }
      });
    });

    const hasMissingComments = Array.from(daysWithHours).some(
      (index) => !dailyComments[index]
    );

    if (hasMissingComments) {
      toast.error("Comments are mandatory for each day with hours.", {
        position: toast.POSITION.TOP_CENTER,
      });
      setLoading(false);
      return;
    }

    const weekNumber = getWeekNumber(moment(startDate).startOf("isoWeek").toDate());
    const payload = {
      EmployeeId: user?.EmployeeId,
      WeekStartDate: moment(startDate).format("YYYY-MM-DD"),
      WeekEndDate: moment(endDate).format("YYYY-MM-DD"),
      WeekNumber: weekNumber,
      TotalHours: totalHours,
      Status: "Submitted",
      lstTimeSheetDetails: projects.flatMap((project) =>
        project.weekDays.map((day, index) => {
          const weekDate = new Date(startDate);
          weekDate.setDate(weekDate.getDate() + index);

          return {
            ProjectCode: project.projectCode,
            ProjectName: project.projectName,
            WeekDate: moment(weekDate).format("YYYY-MM-DD"),
            WeekDay: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][index],
            WeekDayHours: day.hours || 0,
            UserComments: dailyComments[index] || "",
          };
        })
      ),
    };

    try {
      const path = "/submitTimeSheet";
      const URL = process.env.REACT_APP_BE_URL + path;
      const response = await axios.post(URL, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response.status === 200) {
        toast.success("Timesheet submitted successfully!", {
          position: toast.POSITION.TOP_CENTER,
        });
        handleReset();
        navigate("/ViewTimesheet");
      } else {
        handleErrors(response);
      }
    } catch (error) {
      if (error.response) {
        handleErrors(error.response);
      } else {
        toast.error("Error submitting timesheet.", {
          position: toast.POSITION.TOP_CENTER,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    const resetProjects = projects.map((project) => ({
      ...project,
      weekDays: project.weekDays.map((day) => ({ ...day, hours: "" })),
    }));
    setProjects(resetProjects);
    setTotalHours(0);
    setDailyComments(Array(7).fill(""));
    closePopup(); 
  };

  const [showCommentPopup, setShowCommentPopup] = useState(null);

  const handleCommentClick = (dayIndex) => {
    setShowCommentPopup(dayIndex);
    setTempComment(dailyComments[dayIndex] || ""); // Initialize with existing comment
  };

  const closePopup = () => {
    setShowCommentPopup(null);
    setTempComment(""); // Clear temporary comment
  };

  const addComment = (dayIndex) => {
    if (!tempComment.trim()) {
      toast.error("Comment cannot be empty.", {
        position: toast.POSITION.TOP_CENTER,
      });
      return;
    }
    setDailyComments((prevComments) =>
      prevComments.map((comment, index) =>
        index === dayIndex ? tempComment : comment
      )
    );
    toast.success("Comment saved!", {
      position: toast.POSITION.TOP_CENTER,
    });
    closePopup();
  };

  return (
    <div>
      {loading && (
        <div id="preloading">
          <span className="loader"></span>
        </div>
      )}
      <ToastContainer />
      <div className="content-page">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-12">
              <div className="row mb-2">
                <div className="col">
                  <h3 className="title mb-2">Submit Timesheet</h3>
                </div>
                <div className="col-auto">
                  <a
                    href={"/SubmitLeave"}
                    data-extra-toggle="tab"
                    data-target="#leave-management-pan"
                    className="btn btn-primary mt-lg-0 mt-4"
                  >
                    <i className="ri-calendar-schedule-line"></i> Apply Leave
                  </a>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="card mb-4">
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-12">
                      <div className="row justify-content-center justify-content-lg-between align-items-center">
                        <div className="col-lg-2 col-md-3 col-sm-auto d-none d-lg-block "></div>
                        <div className="col-lg-8 col-md-9">
                          <div className="week-picker-container">
                            <WeekPicker
                              startDate={startDate}
                              setStartDate={setStartDate}
                              endDate={endDate}
                              setEndDate={setEndDate}
                              projects={projects}
                              setProjects={setProjects}
                              setIsSubmit={setIsSubmit}
                            />
                          </div>
                          <div className="row align-items-center justify-content-center my-3">
                            <div className="col-lg-12 col-md-12 text-center">
                              <div className="btn-group btn-group-toggle" data-toggle="buttons">
                                <label className="btn btn-outline-primary active">
                                  <input
                                    type="radio"
                                    name="filterBy"
                                    id="all"
                                    checked={filter === "all"}
                                    onChange={handleFilterChange}
                                  />
                                  All
                                </label>
                                <label className="btn btn-outline-primary">
                                  <input
                                    type="radio"
                                    name="filterBy"
                                    id="General & Administration"
                                    checked={filter === "General & Administration"}
                                    onChange={handleFilterChange}
                                  />
                                  Administration
                                </label>
                                <label className="btn btn-outline-primary">
                                  <input
                                    type="radio"
                                    name="filterBy"
                                    id="Projects"
                                    checked={filter === "Projects"}
                                    onChange={handleFilterChange}
                                  />
                                  Projects
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="col-lg-2 col-md-3 col-sm-auto">
                          <div className="card ww-app-bg submit-time-icon">
                            <div className="card-body">
                              <span>
                                <i className="ri-calendar-schedule-fill"></i>
                                {parseInt(totalHours)}
                              </span>
                              <p className="text-white mb-0">Total Hours</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body p-0">
                  {projects.length === 0 ? (
                    <div className="row">
                      <div className="col">
                        <div className="no-data-found">
                          <i className="ri-clipboard-fill"></i>
                          <div className="bounce">
                            <i className="ri-search-eye-line"></i>
                          </div>
                          <p>No Projects Assigned</p>
                        </div>
                      </div>
                    </div>
                  ) : !isSubmit ? (
                    <div className="row">
                      <div className="col">
                        <div className="table-responsive time-entry-view">
                          <table className="table grid">
                            <colgroup>
                              <col />
                              <col className="weekend-column-highlight" />
                              <col />
                              <col />
                              <col />
                              <col />
                              <col />
                              <col className="weekend-column-highlight" />
                              <col />
                            </colgroup>
                            <thead>
                              <tr>
                                <th>Activity</th>
                                <th>Sun, {moment(startDate).format("DD")}</th>
                                <th>
                                  Mon,{" "}
                                  {moment(startDate)
                                    .add(1, "days")
                                    .format("DD")}
                                </th>
                                <th>
                                  Tue,{" "}
                                  {moment(startDate)
                                    .add(2, "days")
                                    .format("DD")}{" "}
                                </th>
                                <th>
                                  Wed,{" "}
                                  {moment(startDate)
                                    .add(3, "days")
                                    .format("DD")}{" "}
                                </th>
                                <th>
                                  Thu,{" "}
                                  {moment(startDate)
                                    .add(4, "days")
                                    .format("DD")}{" "}
                                </th>
                                <th>
                                  Fri,{" "}
                                  {moment(startDate)
                                    .add(5, "days")
                                    .format("DD")}{" "}
                                </th>
                                <th>
                                  Sat,{" "}
                                  {moment(startDate)
                                    .add(6, "days")
                                    .format("DD")}{" "}
                                </th>
                                <th>
                                  Total <small>(Hours)</small>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {filteredProjects.map((project, projectIndex) => (
                                <tr key={project.projectCode}>
                                  <td className="project-label">
                                    {project.projectName}
                                  </td>
                                  {project?.weekDays.map((day, dayIndex) => (
                                    <td
                                      className={`text-center ${dayIndex === 0 || dayIndex === 6
                                        ? "weekend-field"
                                        : ""
                                        }`}
                                      key={dayIndex}
                                    >
                                      <input
                                        type="number"
                                        className="form-control"
                                        placeholder="0"
                                        value={day.hours}
                                        min={0}
                                        onChange={(e) =>
                                          handleInputChange(
                                            project.projectCode,
                                            dayIndex,
                                            e.target.value
                                          )
                                        }
                                      />
                                    </td>
                                  ))}
                                  <td className="text-center project-hours">
                                    <input
                                      type="text"
                                      className="form-control-plaintext text-center"
                                      value={project.weekDays.reduce(
                                        (sum, day) => sum + (day.hours || 0),
                                        0
                                      )}
                                      readOnly
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td>Notes</td>
                                {Array(7)
                                  .fill(null)
                                  .map((_, index) => (
                                    <td
                                      className={`text-center ${index === 0 || index === 6
                                        ? "weekend-field"
                                        : ""
                                        }`}
                                      key={index}
                                    >
                                      <button
                                        className="btn btn-link"
                                        style={{ color: "#1e9533" }}
                                        onClick={() => handleCommentClick(index)}
                                      >
                                        {dailyComments[index] ? "Edit Notes" : "Add Notes"}
                                      </button>
                                      {showCommentPopup === index && (
                                        <div className="comment-popup">
                                          <div className="popup-header">
                                            <h5>
                                              {moment(startDate)
                                                .add(index, "days")
                                                .format("dddd, MMMM D, YYYY")}
                                            </h5>
                                          </div>
                                          <textarea
                                            className="form-control"
                                            value={tempComment}
                                            onChange={(e) => handleCommentChange(e.target.value)}
                                            maxLength={250}
                                            placeholder="Enter your comment here..."
                                          />
                                          <div className="button-group">
                                            <button
                                              className="btn btn-primary mt-2"
                                              onClick={closePopup}
                                            >
                                              Close
                                            </button>
                                            <button
                                              className="btn btn-success mt-2 ml-2"
                                              onClick={() => addComment(index)}
                                            >
                                              Save
                                            </button>
                                          </div>
                                        </div>
                                      )}
                                    </td>
                                  ))}
                                <td></td>
                              </tr>
                              <tr>
                                <td className="text-success">Total Hours</td>
                                {Array(7)
                                  .fill(null)
                                  .map((_, index) => (
                                    <td className="text-center" key={index}>
                                      <input
                                        type="text"
                                        className="form-control-plaintext text-center"
                                        value={projects.reduce(
                                          (sum, project) => {
                                            const hours =
                                              project.weekDays[index] &&
                                              project.weekDays[index].hours;
                                            return sum + (hours || 0);
                                          },
                                          0
                                        )}
                                        readOnly
                                      />
                                    </td>
                                  ))}
                                <td className="text-center">{totalHours}</td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="row">
                      <div className="col">
                        <div className="no-data-found">
                          <i className="ri-calendar-fill"></i>
                          <div className="bounce ripple">
                            <i className="ri-checkbox-circle-fill"></i>
                          </div>
                          <p>Your timesheet has been successfully submitted.</p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="row fixed-footer">
        <div className="col-lg-12 text-center">
          <button className="btn btn-light" type="reset" onClick={handleReset}>
            Reset
          </button>
          <button className="btn btn-primary" type="button" onClick={handleSubmit}>
            Submit <i className="ri-checkbox-circle-line"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubmitTimesheet;